import React, { useRef, useState } from 'react';
import { JarallaxOptions } from 'jarallax';
import classnames from 'classnames';

import type { BannerType } from 'src/components/PageBuilder/components';
import { RichText } from 'src/components/RichText/RichText';
import { Button } from 'src/components/Button/Button';
import { useBreakpointMatch } from 'src/hooks/useBreakpoint';

import { useJarallax } from '../../hooks';
import type { CustomCSSProperties } from '../../../types/pbTypes';

import classes from './Banner.module.scss';

type BannerProps = BannerType & {
    styles: CustomCSSProperties;
};

/**
 * Return the PB Banner
 * @param props
 * @constructor
 */
export function Banner(props: BannerProps) {
    const [hovered, setHovered] = useState(false);
    const toggleHover = () => setHovered(!hovered);

    const { isDesk } = useBreakpointMatch();
    const backgroundImage = isDesk ? props.desktopImage : props.mobileImage;

    /**
     * Configure the styles requires for the Block
     */
    const backgroundElement = useRef<HTMLDivElement | null>(null);
    const hasVideoOverlay = props.videoOverlayColor !== undefined;
    const videoOverlayStyles = {
        backgroundColor: props.videoOverlayColor,
    };

    const hasOverlay = (props.overlayColor?.length ?? 0) > 0 && props?.showOverlay !== 'never';
    const overlayStyles = {
        backgroundColor: props.overlayColor,
    };

    if (props.showOverlay === 'hover') {
        overlayStyles.backgroundColor = 'transparent';
    }

    if (hovered && props.showOverlay === 'hover') {
        overlayStyles.backgroundColor = props.overlayColor;
    }

    /**
     * If the background type is video or parallax is enabled, we need to enable the parallax effect
     */
    const isParallaxEnabled = Boolean(props.backgroundType === 'video' || props.enableParallax);
    const parallaxSpeed = !props.enableParallax ? 1 : props.parallaxSpeed;

    const config: JarallaxOptions = {
        imgPosition: props.backgroundPosition,
        imgRepeat: props.backgroundRepeat === 'no-repeat' ? 'no-repeat' : 'repeat',
        imgSize: props.backgroundSize,
        imgSrc: backgroundImage,
        speed: parallaxSpeed,
        videoLazyLoading: props.videoLazyLoading,
        videoLoop: props.videoLoop,
        videoPlayOnlyVisible: props.videoPlayOnlyVisible,
        videoSrc: props.videoSrc,
    };

    useJarallax({
        isEnabled: isParallaxEnabled,
        backgroundElement,
        config,
        backgroundType: props.backgroundType,
    });

    const bannerClassNames = ['pageBuilderBannerGlobal'];

    if (props.cssClasses && props.cssClasses.length > 0) {
        bannerClassNames.push(...props.cssClasses);
    }

    // Parse the button text and background colours from the props
    const buttonTextColour = props.buttonTextColour ? JSON.parse(props.buttonTextColour) : undefined;

    const buttonBackgroundColour = props.buttonBackgroundColour ? JSON.parse(props.buttonBackgroundColour) : undefined;

    return (
        <div
            data-content-type={props.contentType}
            data-unique-id={props.uniqueID}
            id={props.uniqueID}
            ref={backgroundElement}
            style={props.styles}
            className={classnames({
                [bannerClassNames.join(' ')]: true,
                ['pageBuilderBannerGlobal']: true,
                [classes['root']]: true,
                [classes['rootSlide']]: props.contentType === 'slide',
                [classes['rootPoster']]: props.appearance === 'poster',
            })}
            onMouseEnter={toggleHover}
            onMouseLeave={toggleHover}
            aria-label={props.altText}
            title={props.titleText}
        >
            {hasVideoOverlay && <div className={classes['videoOverlay']} style={videoOverlayStyles} />}
            {props.appearance === 'poster' && <div className={classes['videoOverlay']} style={overlayStyles} />}
            <div
                className={classnames({
                    [classes['container']]: true,
                    [classes['containerLeft']]: props.appearance === 'collage-left',
                    [classes['containerRight']]: props.appearance === 'collage-right',
                    [classes['containerCentered']]: props.appearance === 'collage-centered',
                })}
            >
                {props.content && (
                    <div
                        className={classnames({
                            pageBuilderBannerContentGlobal: true,
                            [classes['content']]: true,
                            [classes['contentBlank']]: !hasOverlay,
                            [classes['contentPoster']]: props.appearance === 'poster',
                            [classes['contentLeft']]: props.appearance === 'collage-left',
                            [classes['contentRight']]: props.appearance === 'collage-right',
                            [classes['contentCentered']]: props.appearance === 'collage-centered',
                        })}
                        style={props.showOverlay !== 'never' && props.appearance !== 'poster' ? overlayStyles : {}}
                    >
                        <RichText content={props.content} />

                        {props.linkType && props.showButton !== 'never' && props.buttonText && (
                            <Button
                                className={classnames({
                                    [classes['contentButton']]: true,
                                    [classes['buttonHover']]: props.showButton === 'hover',
                                })}
                                style={{
                                    backgroundColor: buttonBackgroundColour,
                                    color: buttonTextColour,
                                }}
                                variant={props.buttonType}
                            >
                                {props.buttonText}
                            </Button>
                        )}
                    </div>
                )}
            </div>
        </div>
    );
}
